/*
 *  Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  CloudProviderType,
  Provider,
  useCloudWastePoliciesCategorisedQuery,
  useCloudWastePoliciesQuery,
  useExternalIntegrationsFilterQuery,
  useGetAllAccountsWithDetailsQuery,
} from "~/generated/graphql";
import {
  allRegions,
  commonRegions,
  PROVIDER,
  regions,
} from "~/constants/cloudProviderConst";
import { useCategoriesOptions, useResourceTypeOptions } from "~/hooks";
import { ACTIONS, useAccess } from "~/services/auth";
import {
  getAccountIconStatus,
  isEmpty,
  isNotEmpty,
  isNotNil,
  sort,
} from "~/tools";
import { FiltersOptions, FiltersProp } from "../types";

export function useDataOptions(filters: FiltersProp): FiltersOptions {
  const {
    options: resourceTypes,
    loading: loadingResourceTypes,
    error: resourceTypesError,
  } = useResourceTypeOptions([], filters.provider, filters.category);

  const {
    options: categories,
    loading: loadingCategories,
    error: categoriesError,
  } = useCategoriesOptions();

  const { externalIntegrations, loading: loadingExternalIntegrations } =
    useExternalIntegrations();

  const {
    cloudWastePolicies,
    cloudWastePoliciesCategorised,
    loading: loadingCloudWaste,
    error: cloudWasteError,
    loadingCloudWastePoliciesCategorised,
    errorCloudWastePoliciesCategorised,
  } = useCloudWastePolicies();

  const {
    accountsOptions,
    loading: loadingAccounts,
    error: accountsError,
  } = useAccountsOptions();

  const { regionOptions } = useRegionOptions(filters.provider);

  return {
    accountsOptions,
    regionOptions,
    resourceTypes,
    categories,
    cloudWastePolicies,
    cloudWastePoliciesCategorised,
    externalIntegrations,
    error:
      accountsError ??
      resourceTypesError ??
      cloudWasteError ??
      categoriesError ??
      errorCloudWastePoliciesCategorised,
    loadingExternalIntegrations,
    loadingCategories,
    loadingCloudWaste,
    loadingResourceTypes,
    loadingAccounts,
    loadingCloudWastePoliciesCategorised,
  };
}

function useCloudWastePolicies() {
  const { checkAccess } = useAccess();
  const {
    data: cloudWaste,
    loading,
    error,
  } = useCloudWastePoliciesQuery({
    skip: !checkAccess(ACTIONS.visitSavingsSuggestions),
  });

  const cloudWasteSummaryPolicies =
    cloudWaste?.cloudWasteSummary?.policies ?? [];

  const cloudWastePolicies = cloudWasteSummaryPolicies.map((policy) => ({
    value: policy?.id,
    label: policy?.title,
  }));

  const {
    data: cloudWastePoliciesCat,
    loading: loadingCloudWastePoliciesCategorised,
    error: errorCloudWastePoliciesCategorised,
  } = useCloudWastePoliciesCategorisedQuery({
    skip: !checkAccess(ACTIONS.visitSavingsSuggestions),
  });

  const cloudWastePoliciesCategorised =
    cloudWastePoliciesCat?.cloudWastePoliciesCategorised ?? [];

  return {
    cloudWastePolicies,
    cloudWastePoliciesCategorised,
    loading,
    error,
    loadingCloudWastePoliciesCategorised,
    errorCloudWastePoliciesCategorised,
  };
}

function useAccountsOptions(providers: FiltersProp["provider"] = []) {
  const { data, loading, error } = useGetAllAccountsWithDetailsQuery();

  const accounts = (data?.accounts ?? []).filter(isNotNil);

  const accountsOptions = accounts
    .filter(
      (account) =>
        isEmpty(providers) ||
        providers.find((filtersProvider) =>
          [account.provider, account.providerType].includes(filtersProvider)
        )
    )
    .map((account) => ({
      value: account.providerId ?? account.id,
      label: account.displayName ?? account.name,
      status: getAccountIconStatus({
        provider: account.provider,
        active: account.activated,
        status: account.status ?? "",
        providerType: account.providerType ?? CloudProviderType.Cloud,
      }),
    }));

  return {
    accountsOptions,
    loading,
    error,
  };
}

function useRegionOptions(providers: FiltersProp["provider"] = []) {
  const mappedProviders = mapProviders(providers);
  const mappedRegions = isNotEmpty(providers)
    ? mappedProviders?.flatMap((provider) => regions.get(provider) ?? "") ?? []
    : allRegions.flat();

  const regionOptions = sort([...commonRegions, ...mappedRegions]).map(
    (region) => ({
      value: region,
      label: region,
    })
  );

  return { regionOptions };
}

function useExternalIntegrations() {
  const { checkAccess } = useAccess();
  const { data, ...queryState } = useExternalIntegrationsFilterQuery({
    skip: !checkAccess(ACTIONS.visitExternalIntegrations),
  });

  const externalIntegrations =
    data?.externalIntegrations?.integrations?.map((integration) => ({
      value: integration?.id ?? "",
      label: integration?.name ?? "",
    })) ?? [];

  return {
    externalIntegrations,
    ...queryState,
  };
}

function mapProviders(input: FiltersProp["provider"]): PROVIDER[] | undefined {
  if (input == null) {
    return undefined;
  }

  return input
    .map((provider) => {
      switch (provider) {
        case Provider.Aws:
          return PROVIDER.AWS;
        case Provider.Gcp:
          return PROVIDER.GCP;
        case Provider.Azure:
          return PROVIDER.AZURE;
        case Provider.Ibmcloud:
          return PROVIDER.IBMCLOUD;
        default:
          return undefined;
      }
    })
    .filter(isNotNil);
}
